/**
 * Node Positioning
 * 
 * Main orchestrator for calculating absolute positions for nodes within their layers.
 * 
 * POSITIONING PHASES:
 * 1. Critical Path: Main flow nodes positioned first
 * 2. Feedback Loops: Back-edge nodes positioned to the side, level by level
 * 3. Child-alignment: Parentless nodes re-centered above their children
 * 
 * @see criticalPathPositioning.js - Critical path layout logic
 * @see feedbackPositioning.js - Feedback loop positioning logic
 * @see positioningUtils.js - Shared utility functions
 */

import { separateOverlappingHorizontalSegments } from './segmentSeparation';
import { positionCriticalPath } from './criticalPathPositioning';
import { positionFeedbackNodes } from './feedbackPositioning';
import { resolveLayerOverlaps } from './positioningUtils';
import { getNodeDimensions } from './constants';

/**
 * Position nodes within their layers (VERTICAL FLOW)
 * 
 * @param {Object} layerData - Contains layers array and feedbackLayerInfo map
 * @param {Object} graph - Graph with parent/child relationships and node levels
 * @param {Object} nodeDimensions - Map of node ID to { width, height }
 * @returns {Object} Map of node ID to { x, y } positions
 */
export function positionNodes(layerData, graph, nodeDimensions) {
  const { layers, feedbackLayerInfo } = layerData;

  // PHASE 1: Position CRITICAL PATH nodes
  const { positions, layerYPositions } = positionCriticalPath(layers, graph, nodeDimensions);

  // PHASE 2: Position FEEDBACK nodes level by level
  if (feedbackLayerInfo && feedbackLayerInfo.size > 0) {
    positionFeedbackNodes(positions, feedbackLayerInfo, layerYPositions, graph, nodeDimensions);
  }

  // PHASE 3: Re-center parentless nodes above their children.
  // After pull-down, some nodes end up in deeper layers with no parents.
  // The top-to-bottom positioning pass couldn't align them with children
  // (children weren't positioned yet). Now that everything is positioned,
  // we shift these nodes horizontally to center above their children,
  // which minimises edge crossings and long diagonal lines.
  alignParentlessNodesWithChildren(layers, positions, graph, nodeDimensions);

  // POST-PROCESSING: Separate overlapping horizontal segments (critical path only)
  separateOverlappingHorizontalSegments(layers, positions, graph, nodeDimensions);

  // POST-PROCESSING: Final overlap resolution (critical path only)
  layers.forEach(layer => {
    resolveLayerOverlaps(layer, positions, nodeDimensions);
  });

  return positions;
}

/**
 * Shift parentless nodes (pulled-down roots) to center above their children.
 * 
 * A "parentless" node in a non-first layer has no forward parents in the graph
 * (it was pulled down from layer 0 to sit near its children). During the
 * top-to-bottom positioning pass it got a default X position. Now that children
 * are positioned, we can center it above them for a cleaner layout.
 */
function alignParentlessNodesWithChildren(layers, positions, graph, nodeDimensions) {
  for (let li = 0; li < layers.length; li++) {
    for (const nodeId of layers[li]) {
      const parents = graph.parents.get(nodeId) || [];
      // Only adjust nodes with no forward parents (pulled-down roots)
      if (parents.length > 0) continue;
      // Skip nodes in the very first layer — they're true roots positioned normally
      if (li === 0) continue;
      
      const children = graph.children.get(nodeId) || [];
      if (children.length === 0) continue;
      
      // Calculate the center of all positioned children
      const childCenters = [];
      for (const cid of children) {
        if (positions[cid]) {
          const childWidth = getNodeDimensions(cid, nodeDimensions).width;
          childCenters.push(positions[cid].x + childWidth / 2);
        }
      }
      
      if (childCenters.length === 0) continue;
      
      const avgChildCenter = childCenters.reduce((a, b) => a + b, 0) / childCenters.length;
      const nodeWidth = getNodeDimensions(nodeId, nodeDimensions).width;
      const idealX = avgChildCenter - nodeWidth / 2;
      
      // Move the node (overlap resolution will clean up any collisions)
      positions[nodeId] = {
        ...positions[nodeId],
        x: idealX,
      };
    }
  }
}

// Re-export for backwards compatibility
export { resolveLayerOverlaps } from './positioningUtils';
